import type { ReactNode } from 'react';
import { Button } from '@newcore/ui/buttons';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Sizes } from '@newcore/ui/enums/sizes';
import { DisplaySizes } from '@newcore/ui/enums/typography-display-sizes';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { Display, Text } from '@newcore/ui/typography';
import { Onboarding } from '@/features/onboarding/components/primitives';
import { useMobileInstallApp } from '@/features/onboarding/hooks/use-mobile-install-app';
import {
  getAppStoreBadgeUrl,
  getNewcoreAppIconUrl,
  getPlayStoreBadgeUrl,
} from '@/features/onboarding/utils/install-page-assets';
import { StoreBadge } from './store-badge';

const PLAY_STORE_BADGE_ALT = 'Get it on Google Play';
const APP_STORE_BADGE_ALT = 'Download on the App Store';
const APP_NOT_INSTALLED_NOTICE =
  'The NewCore app isn’t installed on this device. Install it, then tap Open NewCore again.';
const DEFAULT_CTA_LABEL = 'Open NewCore';

export interface StoreSlots {
  storeName: string;
  storeBadges: ReactNode;
  isIos: boolean;
}

interface MobileInstallAppProps {
  deepLinkUrl: string;
  title: string;
  children: (slots: StoreSlots) => ReactNode;
  tenantDisplayName?: string;
  ctaLabel?: string;
  notice?: string;
  playStoreReferrer?: string;
}

function storeName(showAppStore: boolean, showPlayStore: boolean) {
  if (showAppStore && showPlayStore) {
    return 'the App Store or Google Play';
  }

  return showAppStore ? 'the App Store' : 'Google Play';
}

export function MobileInstallApp({
  deepLinkUrl,
  title,
  tenantDisplayName,
  ctaLabel = DEFAULT_CTA_LABEL,
  notice,
  playStoreReferrer,
  children,
}: MobileInstallAppProps) {
  const {
    playStoreUrl,
    appStoreUrl,
    showPlayStore,
    showAppStore,
    isAppMissing,
    handleOpenApp,
  } = useMobileInstallApp({ deepLinkUrl, playStoreReferrer });

  const storeBadges = (
    <div className='flex flex-col items-start gap-2' data-testid='store-badges'>
      {showAppStore && (
        <StoreBadge
          href={appStoreUrl}
          src={getAppStoreBadgeUrl()}
          alt={APP_STORE_BADGE_ALT}
          testId='app-store-badge'
        />
      )}
      {showPlayStore && (
        <StoreBadge
          href={playStoreUrl}
          src={getPlayStoreBadgeUrl()}
          alt={PLAY_STORE_BADGE_ALT}
          testId='play-store-badge'
        />
      )}
    </div>
  );

  return (
    <div className='h-dvh overflow-y-auto bg-surface-100'>
      <div className='mx-auto flex min-h-full w-full max-w-md flex-col px-6 pb-6 pt-10 text-center'>
        <div className='flex flex-col items-center gap-5'>
          <img
            src={getNewcoreAppIconUrl()}
            alt='NewCore'
            className='size-20 rounded-[18px]'
          />
          <div className='flex flex-col gap-2.5'>
            <Display
              as='h1'
              size={DisplaySizes.SM}
              weight={FontWeights.REGULAR}
              className='font-manrope leading-[1.2] tracking-[-0.28px] text-ink-800'
            >
              {title}
            </Display>
            {tenantDisplayName && (
              <Text
                as='p'
                size={TextSizes.MD}
                className='font-inter leading-[1.4] tracking-[-0.16px] text-ink-500'
                data-testid='mobile-install-app-subtitle'
              >
                You were invited to {tenantDisplayName}.
              </Text>
            )}
          </div>
        </div>

        {notice && (
          <Text
            as='p'
            size={TextSizes.MD}
            className='mt-4 text-tertiary'
            data-testid='mobile-install-app-notice'
          >
            {notice}
          </Text>
        )}

        {isAppMissing && (
          <Onboarding.Error
            error={APP_NOT_INSTALLED_NOTICE}
            className='mt-6 text-left'
          />
        )}

        <ol
          className='mt-8 flex flex-col gap-[18px] rounded-[20px] bg-surface-50 p-5 text-left'
          data-testid='install-steps'
        >
          {children({
            storeName: storeName(showAppStore, showPlayStore),
            storeBadges,
            isIos: showAppStore && !showPlayStore,
          })}
        </ol>

        <div className='mt-auto pt-8'>
          <Button
            size={Sizes.LG}
            onClick={handleOpenApp}
            className='h-auto w-full rounded-[40px] bg-ink-800 px-5 py-3.5 text-white hover:not-disabled:bg-ink-800'
            data-testid='open-app-button'
          >
            <Text
              as='span'
              size={TextSizes.LG}
              weight={FontWeights.SEMIBOLD}
              className='font-manrope leading-[22px]'
            >
              {ctaLabel}
            </Text>
          </Button>
        </div>
      </div>
    </div>
  );
}
